/** Helpers for the lead card carousel module. */

import type { LeadData } from './types';
import { crmLeadsApi } from '@/lib/api/services/crmLeads';

/** Format CRM recall timestamp for the pending-dashboard cards */
export function formatRecallAtLabel(iso: string | null): string {
  if (!iso) return 'No recall time set';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export const parseTags = (tags: string[] | string) => {
  if (Array.isArray(tags)) return tags;
  if (typeof tags === "string") return tags.split(",").map(tag => tag.trim());
  return [];
};

/** Resolve CRM record primary key for GET-by-id and realtime matching. */
export function resolveLeadRecordId(
  lead: { id?: unknown; record_id?: unknown } | null | undefined,
): number | null {
  if (!lead) return null;
  for (const candidate of [lead.record_id, lead.id]) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

export function tasksSignature(tasks: unknown): string {
  try {
    return JSON.stringify(tasks ?? null);
  } catch {
    return String(tasks);
  }
}

/** Normalize API lead response to LeadData shape (for fresh fetch when card opened from table) */
export const normalizeApiLeadToLeadData = (apiLead: any): LeadData => {
  const d = apiLead?.data || {};
  const recordId = resolveLeadRecordId(apiLead) ?? apiLead?.id;
  return {
    id: recordId,
    created_at: apiLead.created_at ?? '',
    name: apiLead.name ?? d.name ?? 'N/A',
    email: apiLead.email ?? d.email ?? '',
    phone: apiLead.phone ?? apiLead.phone_number ?? d.phone_number ?? d.phone_no ?? d.phone ?? '',
    phone_no: apiLead.phone_no ?? apiLead.phone_number ?? d.phone_number ?? d.phone_no ?? d.phone ?? '',
    phone_number: apiLead.phone_number ?? d.phone_number ?? d.phone_no ?? d.phone ?? '',
    company: apiLead.company ?? d.company ?? '',
    position: apiLead.position ?? d.position ?? '',
    source: apiLead.source ?? d.lead_source ?? d.source ?? '',
    lead_source: apiLead.lead_source ?? d.lead_source ?? '',
    status: apiLead.status ?? apiLead.lead_stage ?? d.lead_stage ?? d.lead_status ?? 'New',
    notes: apiLead.notes ?? d.notes ?? d.latest_remarks ?? '',
    budget: apiLead.budget ?? d.budget ?? 0,
    location: apiLead.location ?? d.location ?? d.state ?? '',
    tags: Array.isArray(apiLead.tags) ? apiLead.tags : (d.tags ? (Array.isArray(d.tags) ? d.tags : []) : []),
    display_pic_url: apiLead.display_pic_url ?? d.display_pic_url ?? null,
    linkedin_profile: apiLead.linkedin_profile ?? d.linkedin_profile ?? '',
    website: apiLead.website ?? d.website ?? '',
    next_follow_up: apiLead.next_follow_up ?? d.next_follow_up ?? d.next_call_at ?? '',
    lead_stage: apiLead.lead_stage ?? d.lead_stage ?? d.lead_status ?? 'New',
    // Never fall back to user_id — can collide with highlight / open-by-praja.
    praja_id: apiLead.praja_id ?? d.praja_id ?? '',
    affiliated_party: apiLead.affiliated_party ?? d.affiliated_party ?? '',
    rm_dashboard: apiLead.rm_dashboard ?? d.rm_dashboard ?? '',
    user_profile_link: apiLead.user_profile_link ?? d.user_profile_link ?? '',
    whatsapp_link: apiLead.whatsapp_link ?? d.whatsapp_link ?? '',
    package_to_pitch: apiLead.package_to_pitch ?? d.package_to_pitch ?? '',
    premium_poster_count: apiLead.premium_poster_count ?? d.premium_poster_count ?? 0,
    last_active_date: apiLead.last_active_date ?? d.last_active_date ?? '',
    last_active_date_time: apiLead.last_active_date_time ?? d.last_active_date_time ?? '',
    latest_remarks: apiLead.latest_remarks ?? d.latest_remarks ?? '',
    tasks: apiLead.tasks ?? d.tasks ?? [],
    data: {
      ...d,
      name: d.name ?? apiLead.name ?? 'N/A',
      phone_number: d.phone_number ?? apiLead.phone_number ?? '',
      lead_stage: d.lead_stage ?? apiLead.lead_stage ?? 'New',
      praja_id: d.praja_id ?? apiLead.praja_id ?? '',
      tasks: apiLead.tasks ?? d.tasks,
    },
  };
};

export const persistActionButtonsState = (leadId: string | number | undefined, visible: boolean) => {
  try {
    if (leadId && visible) {
      sessionStorage.setItem('leadCardCarousel_actionButtonsVisible', JSON.stringify({ leadId: String(leadId), visible }));
    } else {
      sessionStorage.removeItem('leadCardCarousel_actionButtonsVisible');
    }
  } catch (e) {
    console.warn('[LeadCardCarousel] Failed to persist action buttons state:', e);
  }
};

export const restoreActionButtonsState = (leadId: string | number | undefined): boolean => {
  try {
    if (!leadId) return false;
    const stored = sessionStorage.getItem('leadCardCarousel_actionButtonsVisible');
    if (stored) {
      const { leadId: storedLeadId, visible } = JSON.parse(stored);
      if (String(storedLeadId) === String(leadId) && visible) {
        return true;
      }
    }
    return false;
  } catch {
    return false;
  }
};

const LEAD_START_TIME_KEY = 'leadCardCarousel_leadStartTime';

// How long a persisted start time stays valid for its original lead id —
// purely about the on-screen "On This Lead" display timer surviving a page
// refresh, nothing to do with preventing concurrent leads (that's now
// handled server-side, see record_lead_opened in pyro-backend/.../
// rm_activity.py — an RM can only ever have one open session there,
// enforced at the source instead of blocked client-side). Generous enough
// that no single lead legitimately stays open this long, short enough that
// a device left on an undispositioned lead overnight doesn't resurrect a
// stale multi-day-old timer if that same lead id ever comes back around.
const LEAD_START_TIME_MAX_AGE_MS = 12 * 60 * 60 * 1000;

interface StoredLeadStartTime {
  leadId: string;
  startedAt: string;
  /** Which page/carousel instance opened this lead (config.title, or a
   * fallback) — carried onto the touch report's "Opened From" column. */
  pageTitle: string;
}

const readStoredRecord = (): StoredLeadStartTime | null => {
  try {
    const stored = localStorage.getItem(LEAD_START_TIME_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as Partial<StoredLeadStartTime>;
    if (!parsed.leadId || !parsed.startedAt) return null;
    const startedAt = new Date(parsed.startedAt);
    if (Number.isNaN(startedAt.getTime())) return null;
    if (Date.now() - startedAt.getTime() > LEAD_START_TIME_MAX_AGE_MS) return null;
    return {
      leadId: String(parsed.leadId),
      startedAt: parsed.startedAt,
      pageTitle: parsed.pageTitle || 'another page',
    };
  } catch {
    return null;
  }
};

// localStorage, not sessionStorage: sessionStorage is tied to the tab's own
// browsing-context lifetime, and Chrome can discard/reclaim a backgrounded
// tab's memory while the RM is on other tabs doing other things — on
// restore, sessionStorage comes back empty, silently resetting the on-screen
// "On This Lead" timer to 0 even though the lead has been open the whole
// time. localStorage survives that.
const readStoredLeadStartTime = (leadId: string | number | undefined): Date | null => {
  if (!leadId) return null;
  const record = readStoredRecord();
  if (!record || record.leadId !== String(leadId)) return null;
  return new Date(record.startedAt);
};

// Reuses the persisted start time for this lead if one exists — survives a
// page refresh (the exact "fetch current assigned lead on refresh" flow in
// useLeadCardCarousel), a same-lead realtime re-fetch, and the tab being
// backgrounded/discarded while the RM is on other tabs, all of which used
// to stamp a fresh `new Date()` and silently turn duration_seconds (sent on
// disposition, feeds RM PRD's ACHT) into "how long has this tab been open"
// instead of real time-on-lead. Falls back to "now" for a genuinely new
// lead id, or for a stale entry older than LEAD_START_TIME_MAX_AGE_MS.
// (This is the display timer only — the authoritative duration sent to the
// backend comes from the server-side session in record_lead_opened, which
// isn't affected if this client value ever drifts.)
export const getOrCreateLeadStartTime = (leadId: string | number | undefined, pageTitle = 'another page'): Date => {
  if (!leadId) return new Date();
  const existing = readStoredLeadStartTime(leadId);
  if (existing) return existing;

  const now = new Date();
  try {
    localStorage.setItem(
      LEAD_START_TIME_KEY,
      JSON.stringify({ leadId: String(leadId), startedAt: now.toISOString(), pageTitle })
    );
  } catch (e) {
    console.warn('[LeadCardCarousel] Failed to persist lead start time:', e);
  }
  return now;
};

// Stamps the server-side "lead opened" moment the first time this lead id
// is seen (or re-seen after its local start time went stale) — mirrors
// getOrCreateLeadStartTime's own storage guard so a page refresh, a
// same-lead realtime re-fetch, a parent re-render, or a tab discard/restore
// never re-fires it for a lead already open. Always succeeds immediately —
// there's no cross-page lock to check anymore; if the RM already had a
// DIFFERENT lead open, the backend (record_lead_opened) abandons that old
// session the moment this event arrives, rather than this function ever
// blocking the lead card from opening. Fire-and-forget: sendLeadEvent never
// throws.
export const markLeadOpened = (leadId: string | number | undefined, pageTitle = 'another page'): Date => {
  const alreadyStored = readStoredLeadStartTime(leadId) !== null;
  const startTime = getOrCreateLeadStartTime(leadId, pageTitle);

  if (!alreadyStored && leadId) {
    void crmLeadsApi.sendLeadEvent('lead.opened', Number(leadId), { opened_from: pageTitle });
  }

  return startTime;
};

// Releases this lead's display-timer record — called both right after a
// successful disposition (duration_seconds already sent) AND whenever the
// RM stops holding this lead WITHOUT dispositioning it (modal closed,
// navigated away — see useLeadCardCarousel.ts's unmount-cleanup effect), so
// a later, genuinely new visit to the same lead id (re-queued after Call
// Back Later, or just reopened after being abandoned) starts its own fresh
// timer instead of inheriting this one.
export const clearLeadStartTime = (leadId: string | number | undefined) => {
  try {
    const stored = localStorage.getItem(LEAD_START_TIME_KEY);
    if (!stored) return;
    const { leadId: storedLeadId } = JSON.parse(stored);
    if (String(storedLeadId) === String(leadId)) {
      localStorage.removeItem(LEAD_START_TIME_KEY);
    }
  } catch {
    // best-effort cleanup — a leftover entry just gets overwritten next visit
  }
};

export const getTodayDateString = (): string => {
  const now = new Date();
  return now.toISOString().split('T')[0];
};

export const shouldResetFetchedCount = (): boolean => {
  try {
    const lastResetDate = localStorage.getItem('leadCardCarousel_lastResetDate');
    const today = getTodayDateString();
    return lastResetDate !== today;
  } catch {
    return true;
  }
};

export const getLeadName = (lead: LeadData | null): string => {
  if (!lead) return "N/A";
  return lead.data?.name || lead.name || "N/A";
};

/** Display and copy: digits only, no spaces. Indian 91+10 digits shown as 10 digits. */
export const formatPhoneForDisplay = (phone?: string) => {
  if (!phone) return "N/A";
  const digits = phone.replace(/\D/g, "");
  if (!digits) return "N/A";
  if (digits.length === 12 && digits.startsWith("91")) {
    return digits.slice(2);
  }
  return digits;
};

export const normalizePhoneForLinks = (phone?: string) => {
  if (!phone) return "";
  return phone.replace(/\D/g, "");
};

/** For tel: link strip leading 91 so dialer gets 10-digit number */
export const getPhoneForDial = (phone?: string) => {
  const digits = normalizePhoneForLinks(phone);
  if (!digits) return "";
  if (digits.length === 12 && digits.startsWith("91")) {
    return digits.slice(2);
  }
  return digits;
};
